import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "@mui/material";

import { apiErrorMessage } from "../API";
import type { Dispatch, RootState } from "../store";
import { setShareLinksEnabled, shareCall, unshareCall } from "./api";
import type { CallPageData } from "./types";
import "../styles/ui.css";

type Minted = { token: string; expires_at: string };

const formatExpiry = (iso: string) => {
  const when = new Date(iso);
  return Number.isNaN(when.getTime())
    ? iso
    : when.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
};

/**
 * The owner's Share button and its dialog. Opening the dialog never makes a
 * link: link sharing is turned on here, a link is made by "Create link", and a
 * call that is already shared says so in the header and in the dialog. The call
 * page is already behind the `calls` prerelease gate.
 */
export function ShareControl({ botId, shared }: { botId: string; shared: CallPageData["shared"] }) {
  const enabled = useSelector((s: RootState) => s.user.share_links_enabled);
  const dispatch = useDispatch<Dispatch>();
  const [open, setOpen] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(shared?.expires_at ?? null);
  const [minted, setMinted] = useState<Minted | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const attempt = async (work: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (reason) {
      if ((reason as { error?: string } | undefined)?.error === "sharing_off") {
        dispatch.user.setShareLinksEnabled(false);
      } else {
        setError(apiErrorMessage(reason, fallback));
      }
    } finally {
      setBusy(false);
    }
  };

  const turnOn = () =>
    attempt(async () => {
      await setShareLinksEnabled(true);
      dispatch.user.setShareLinksEnabled(true);
    }, "Could not turn on link sharing.");

  const create = () =>
    attempt(async () => {
      const link = await shareCall(botId);
      setMinted(link);
      setExpiresAt(link.expires_at);
      setCopied(false);
    }, "Could not create the link.");

  const stop = () =>
    attempt(async () => {
      await unshareCall(botId);
      setMinted(null);
      setExpiresAt(null);
    }, "Could not stop sharing.");

  const copy = async () => {
    if (!minted) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/s/${minted.token}`);
      setCopied(true);
      setError(null);
    } catch {
      setError("Couldn't copy.");
    }
  };

  const close = () => setOpen(false);

  return (
    <>
      {expiresAt && <span className="dg-call-note">Shared · expires {formatExpiry(expiresAt)}</span>}
      <button className="dg-btn dg-btn-ghost dg-btn-sm" type="button" onClick={() => setOpen(true)}>
        Share
      </button>
      <Dialog open={open} onClose={close} aria-labelledby="dg-share-title">
        <div className="dg-card-body">
          <h2 id="dg-share-title" className="dg-card-title">
            Share this call
          </h2>
          {!enabled ? (
            <>
              <p>
                Link sharing is off for you. Turn it on to give anyone with a link read-only access to
                your calls&rsquo; recordings and transcripts.
              </p>
              <button className="dg-btn dg-btn-primary" type="button" disabled={busy} onClick={turnOn}>
                Turn on link sharing
              </button>
            </>
          ) : (
            <>
              {expiresAt && <p>Shared · expires {formatExpiry(expiresAt)}</p>}
              <p>
                Anyone with the link can watch the recording and read the transcript; the people on the
                call weren&rsquo;t asked.
              </p>
              <div className="dg-demo-actions">
                {minted ? (
                  <button className="dg-btn dg-btn-primary" type="button" disabled={busy} onClick={copy}>
                    {copied ? "Copied" : "Copy link"}
                  </button>
                ) : (
                  <button className="dg-btn dg-btn-primary" type="button" disabled={busy} onClick={create}>
                    Create link
                  </button>
                )}
                {expiresAt && (
                  <button className="dg-btn dg-btn-danger" type="button" disabled={busy} onClick={stop}>
                    Stop sharing
                  </button>
                )}
              </div>
            </>
          )}
          {error && <p role="alert">{error}</p>}
          <button className="dg-btn dg-btn-ghost" type="button" onClick={close}>
            Close
          </button>
        </div>
      </Dialog>
    </>
  );
}
